import interact from "interactjs";
import { EventBus, Events } from "../eventBus";
import { getResizableAnchorConstraints } from "./anchors";
import { CSSClass, ElemID } from "./constants";
import { renderFloatingAnchor } from "./renderer";
import { wayfinderGetClosestPage } from "./wayfinder";
import { getCurrentScale } from "./zoom";

export function initInteractions() {
  makeAnchorsDraggable();
  makeAnchorsDroppableOnPages();
}

function makeAnchorsDraggable() {
  // Handle drag on static anchors
  interact("." + CSSClass.STATIC_ANCHOR)
    .draggable({
      manualStart: true, // because we want to drag a dynamically generated elem instead
      inertia: {
        // enable inertia so we get smooth animation when dropping outside of page
        resistance: 100, // high resistance so you can't just toss the anchors about
      },
      modifiers: draggableModifiers,
      listeners: {
        move(event) {
          applyAnchorMove(event.currentTarget, event.dx, event.dy);
        },
      },
    })
    .on("move", function (event) {
      const { currentTarget, interaction } = event;
      const scale = getCurrentScale("editor");
      let element = currentTarget;

      // if this is the start of an interaction
      if (interaction.pointerIsDown && !interaction.interacting()) {
        // if dragging item from sidebar
        if (currentTarget.classList.contains(CSSClass.STATIC_ANCHOR)) {
          // Generate floating anchor and use that as move target instead
          element = renderFloatingAnchor(
            currentTarget,
            event.x,
            event.y,
            scale,
          );
        }
      }

      // Start the drag event
      interaction.start({ name: "drag" }, event.interactable, element);
    });

  // Handle non-resizable floating anchors
  interact("." + CSSClass.MOVABLE_ANCHOR).draggable({
    inertia: {
      // enable inertia so we get smooth animation when dropping outside of page
      resistance: 100, // high resistance so you can't just toss the anchors about
    },
    modifiers: draggableModifiers,
    listeners: {
      move(event) {
        applyAnchorMove(event.currentTarget, event.dx, event.dy);
      },
    },
  });

  const resizableMovable = interact("." + CSSClass.RESIZABLE_MOVABLE_ANCHOR);
  // Handle resizable floating anchors
  resizableMovable
    .draggable({
      inertia: {
        // enable inertia so we get smooth animation when dropping outside of page
        resistance: 100, // high resistance so you can't just toss the anchors about
      },
      modifiers: draggableModifiers,
      listeners: {
        move(event) {
          applyAnchorMove(event.currentTarget, event.dx, event.dy);
        },
      },
    })
    .resizable({
      edges: {
        top: ".resize-top",
        left: ".resize-left",
        bottom: ".resize-bottom",
        right: ".resize-right",
      },
      modifiers: [
        interact.modifiers.aspectRatio({
          // make sure the width is always double the height
          ratio: "preserve",
          // also restrict the size by nesting another modifier
          modifiers: [
            interact.modifiers.restrict({
              restriction: "parent",
            }),
          ],
        }),
      ],
      listeners: {
        move: function (event) {
          const anchorElement = event.currentTarget;
          const scale = getCurrentScale("editor");
          // // resize event is captured by the img sitting on top of div
          // const imgElement = event.currentTarget;
          // const anchorElement = imgElement.parentElement;
          applyAnchorMove(
            anchorElement,
            event.deltaRect.left,
            event.deltaRect.top,
          );

          const constraints = getResizableAnchorConstraints(
            anchorElement.dataset.anchor,
          );

          let newWidth = Math.min(
            constraints.maxWidth * scale,
            Math.max(constraints.minWidth * scale, event.rect.width),
          );
          let newHeight = Math.min(
            constraints.maxHeight * scale,
            Math.max(constraints.minHeight * scale, event.rect.height),
          );
          anchorElement.style.width = `${newWidth}px`;
          anchorElement.style.height = `${newHeight}px`;
        },
      },
    });
}

const draggableModifiers = [
  // restrict target page
  interact.modifiers.restrictRect({
    restriction: function (x, y, target) {
      const element = target.element;
      const scale = getCurrentScale("editor");

      // select element considered "active drop target". See "overlap" field in makeAnchorsDroppableOnPages
      const targetPage = document.querySelector(
        "." + CSSClass.DROP_TARGET_ACTIVE,
      );
      if (targetPage) {
        return targetPage;
      }

      // if element is outside all dropzones, identify its parent page and snap to it
      const parentPageNum = element.dataset.parentPage;
      const parentPage = parentPageNum
        ? document.getElementById(ElemID.PAGE_PREFIX + parentPageNum)
        : undefined;
      if (parentPage) {
        return parentPage;
      }

      // if no parent (new anchor), snap to page closest to the element
      const closestPageId = wayfinderGetClosestPage(element, scale);
      return document.getElementById(closestPageId);
    },
    endOnly: true,
  }),
  // also restrict to page container so we don't drop on page portion that is outside of view
  interact.modifiers.restrictRect({
    restriction: document.getElementById(ElemID.EDITOR_PAGE_CONTAINER),
    endOnly: true,
  }),
];

function applyAnchorMove(element, dx, dy) {
  // update stored elem position relative to origin of parent element
  element.dataset.deltaX = String(Number(element.dataset.deltaX || "0") + dx);
  element.dataset.deltaY = String(Number(element.dataset.deltaY || "0") + dy);
  // apply transformation so elem is position where expected
  element.style.transform = `translate(${element.dataset.deltaX}px, ${element.dataset.deltaY}px)`;
}

function makeAnchorsDroppableOnPages() {
  interact("." + CSSClass.PAGE).dropzone({
    accept: "." + CSSClass.FLOATING_ANCHOR,
    overlap: 0.01, // consider "in zone" when anchor is at least 1% in target zone.
    // overlap: 1, // consider "in zone" when anchor is 100% in target zone
    // overlap: "pointer", // consider "in zone" when mouse target zone

    // when anchor is being moved, we can highlight where potential targets are
    ondropactivate: function (event) {
      // N.B: this is called for each target, so do account for the fact that this is triggered per PDF page.
      event.target.classList.add(CSSClass.DROP_TARGET_CANDIDATES);
    },

    // when interaction over
    ondropdeactivate: function (event) {
      // N.B: this is called for each target, so do account for the fact that this is triggered per PDF page.

      const anchorElement = event.relatedTarget;
      const pageElement = event.target;
      pageElement.classList.remove(CSSClass.DROP_TARGET_CANDIDATES);
    },

    // when anchor enters a specific page
    ondragenter: function (event) {
      const anchorElement = event.relatedTarget;
      const pageElement = event.target;

      // anchorElement.classList.add(CSSClass.ANCHOR_IN_PAGE);
      pageElement.classList.add(CSSClass.DROP_TARGET_ACTIVE);
    },

    // when anchor leaves a page
    ondragleave: function (event) {
      const anchorElement = event.relatedTarget;
      const pageElement = event.target;

      // anchorElement.classList.remove(CSSClass.ANCHOR_IN_PAGE);
      pageElement.classList.remove(CSSClass.DROP_TARGET_ACTIVE);
    },

    // on drop
    ondrop: function (event) {
      const anchorElement = event.relatedTarget;
      const pageElement = event.target;
      // the following will be undefined if anchor not yet assigned to a page
      const currentElementPage = anchorElement.dataset.parentPage;
      const targetPage = pageElement.dataset.pageNumber;

      pageElement.classList.remove(CSSClass.DROP_TARGET_ACTIVE);

      if (!currentElementPage) {
        /** dropping new anchor to a page. We will need to make it a child of page and remap relative positions **/
        console.debug("Adding new anchor to page " + targetPage);

        // calculate current visual position of anchor element relative to (0,0) of viewport
        const anchorX =
          anchorElement.offsetLeft +
          Number(anchorElement.dataset.deltaX || "0");
        const anchorY =
          anchorElement.offsetTop + Number(anchorElement.dataset.deltaY || "0");

        // calculate current visual position of page element relative to (0,0) of viewport
        const pageBoundingRect = pageElement.getBoundingClientRect();
        const pageX = pageBoundingRect.left;
        const pageY = pageBoundingRect.top;

        // map element position to coordinates relative to (0, 0) of page element
        const relativeX = anchorX - pageX;
        const relativeY = anchorY - pageY;

        // Make the page the new parent of the anchor
        pageElement.appendChild(anchorElement);

        // Store parent page number
        anchorElement.dataset.parentPage = targetPage;

        // place elements at origin of new parent, and rely on translations to reposition it
        anchorElement.style.left = "0px";
        anchorElement.style.top = "0px";

        // Translate element to visual position relative to origin of page
        anchorElement.style.transform = `translate(${relativeX}px, ${relativeY}px)`;

        // Store translation on element so "move" event works correctly
        anchorElement.dataset.deltaX = String(relativeX);
        anchorElement.dataset.deltaY = String(relativeY);

        // Emit event
        EventBus.emit(Events.ANCHOR_ADDED, {
          anchorId: anchorElement.id,
          page: targetPage,
        });
      } else if (currentElementPage === targetPage) {
        /** Nothing to do. Anchor moved to a new position within the same page **/
        console.debug("Moving anchor within page " + targetPage);
      } else {
        /** Anchor moved from one page to another **/
        console.debug(
          "Moving anchor from page " +
            currentElementPage +
            " to page " +
            targetPage,
        );

        const oldParentElement = document.getElementById(
          ElemID.PAGE_PREFIX + currentElementPage,
        );
        const newParentElement = pageElement;

        // calculate delta between origin of source page and target page
        const oldParentBoundingRect = oldParentElement.getBoundingClientRect();
        const newParentBoundingRect = newParentElement.getBoundingClientRect();
        const parentDeltaX =
          newParentBoundingRect.left - oldParentBoundingRect.left;
        const parentDeltaY =
          newParentBoundingRect.top - oldParentBoundingRect.top;

        // map element position to coordinates relative to (0, 0) of new page element
        const relativeX = Number(anchorElement.dataset.deltaX) - parentDeltaX;
        const relativeY = Number(anchorElement.dataset.deltaY) - parentDeltaY;

        // Move anchor element to new page
        newParentElement.appendChild(anchorElement);

        // Store parent page number
        anchorElement.dataset.parentPage = targetPage;

        // Translate element to visual position relative to origin of page
        anchorElement.style.transform = `translate(${relativeX}px, ${relativeY}px)`;

        // Store translation on element so "move" event works correctly
        anchorElement.dataset.deltaX = String(relativeX);
        anchorElement.dataset.deltaY = String(relativeY);

        // Emit event
        EventBus.emit(Events.ANCHOR_MOVED, {
          anchorId: anchorElement.id,
          fromPage: currentElementPage,
          page: targetPage,
        });
      }
    },
  });
}
